/**
 * useDailyOffer — Selects ONE admin-curated partner offer for the daily popup.
 *
 * Selection order (deterministic, no AI):
 *   1. Pinned offers / advertisers (always-show)
 *   2. Eligible + currently inside the admin-set window
 *   3. Higher priority wins
 *   4. Tie-break boost if the user has joined the linked community goal
 *   5. Exclude anything this user has seen in the last 7 days
 *   6. Same-advertiser cooldown of 3 days
 *
 * Anything not flagged `daily_offer_eligible` (or pinned) by an admin is invisible.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logger } from "@/utils/logger";

const STORAGE_KEY = "daily_offer_last_shown";
const NEW_USER_GRACE_DAYS = 1;
const RECENT_VIEW_EXCLUSION_DAYS = 7;
const ADVERTISER_COOLDOWN_DAYS = 3;

export type DailyOfferSource = "community_goal" | "advertiser";
export type SelectionReason =
  | "pinned_goal_offer"
  | "pinned_advertiser"
  | "joined_goal_offer"
  | "eligible_goal_offer"
  | "premium_advertiser"
  | "eligible_advertiser";

export interface DailyOffer {
  id: string;
  source: DailyOfferSource;
  title: string;
  description: string | null;
  image_url: string | null;
  code: string | null;
  external_url: string | null;
  sponsor_name: string | null;
  sponsor_logo_url: string | null;
  valid_until: string | null;
  selection_reason: SelectionReason;
  advertiser_id?: string | null;
}

function uaeTodayKey(): string {
  const now = new Date();
  const uae = new Date(now.getTime() + 4 * 60 * 60 * 1000);
  return uae.toISOString().slice(0, 10);
}

function alreadyShownToday(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === uaeTodayKey();
  } catch {
    return false;
  }
}

export function markShownToday() {
  try {
    localStorage.setItem(STORAGE_KEY, uaeTodayKey());
  } catch {
    /* ignore */
  }
}

function inWindow(starts?: string | null, ends?: string | null, today?: string): boolean {
  const t = today ?? new Date().toISOString().slice(0, 10);
  if (starts && starts > t) return false;
  if (ends && ends < t) return false;
  return true;
}

async function selectOffer(userId: string): Promise<DailyOffer | null> {
  const today = new Date().toISOString().slice(0, 10);

  // Recent views (last N days) -> exclude these offer ids
  const since = new Date(Date.now() - RECENT_VIEW_EXCLUSION_DAYS * 86400_000).toISOString();
  const { data: recentViews } = await supabase
    .from("daily_offer_views")
    .select("offer_id, offer_source, viewed_at")
    .eq("user_id", userId)
    .gte("viewed_at", since);

  const excludeOfferIds = new Set((recentViews ?? []).map((r) => r.offer_id));

  // Advertiser cooldown
  const cooldownSince = new Date(Date.now() - ADVERTISER_COOLDOWN_DAYS * 86400_000).toISOString();
  const { data: recentAdv } = await supabase
    .from("daily_offer_views")
    .select("offer_id")
    .eq("user_id", userId)
    .eq("offer_source", "advertiser")
    .gte("viewed_at", cooldownSince);
  const cooldownAdvertiserIds = new Set((recentAdv ?? []).map((r) => r.offer_id));

  // Joined community goals
  const { data: joined } = await supabase
    .from("common_goal_participants")
    .select("common_goal_id")
    .eq("user_id", userId);
  const joinedIds = new Set((joined ?? []).map((r) => r.common_goal_id));

  // --- Fetch curated goal-support offers ---
  const { data: gsoRaw } = await supabase
    .from("goal_support_offers")
    .select(
      "id, title, description, image_url, redemption_message, external_website_url, valid_to, common_goal_id, daily_offer_eligible, daily_offer_priority, daily_offer_starts_at, daily_offer_ends_at, is_pinned, status, is_active",
    )
    .eq("status", "approved")
    .eq("is_active", true)
    .gte("valid_to", today)
    .or("daily_offer_eligible.eq.true,is_pinned.eq.true");

  const gso = (gsoRaw ?? []).filter(
    (o) =>
      !excludeOfferIds.has(o.id) &&
      inWindow(o.daily_offer_starts_at as any, o.daily_offer_ends_at as any, today),
  );

  // --- Fetch curated advertisers ---
  const { data: advRaw } = await supabase
    .from("advertisers")
    .select(
      "id, brand_name, logo_url, promo_code, promo_description, promo_valid_until, affiliate_url, website_url, promo_premium_only, priority, daily_offer_eligible, daily_offer_priority, daily_offer_starts_at, daily_offer_ends_at, is_pinned, is_verified",
    )
    .eq("is_verified", true)
    .not("promo_code", "is", null)
    .or("daily_offer_eligible.eq.true,is_pinned.eq.true");

  const adv = (advRaw ?? []).filter(
    (a) =>
      !excludeOfferIds.has(a.id) &&
      !cooldownAdvertiserIds.has(a.id) &&
      (!a.promo_valid_until || a.promo_valid_until >= today) &&
      inWindow(a.daily_offer_starts_at as any, a.daily_offer_ends_at as any, today),
  );

  // Build scored candidates
  type Candidate = { offer: DailyOffer; score: number };
  const candidates: Candidate[] = [];

  for (const o of gso) {
    const pinned = o.is_pinned === true;
    const joinedBoost = joinedIds.has(o.common_goal_id as any) ? 1000 : 0;
    const score =
      (pinned ? 1_000_000 : 0) + (o.daily_offer_priority ?? 0) * 10 + joinedBoost;
    const reason: SelectionReason = pinned
      ? "pinned_goal_offer"
      : joinedBoost
        ? "joined_goal_offer"
        : "eligible_goal_offer";
    // sponsor lookup is deferred to selected winner only (cheaper)
    candidates.push({
      score,
      offer: {
        id: o.id,
        source: "community_goal",
        title: o.title,
        description: o.description ?? o.redemption_message ?? null,
        image_url: o.image_url,
        code: o.redemption_message ?? null,
        external_url: o.external_website_url,
        sponsor_name: null,
        sponsor_logo_url: null,
        valid_until: o.valid_to,
        selection_reason: reason,
      },
    });
  }

  for (const a of adv) {
    const pinned = a.is_pinned === true;
    const premium = a.promo_premium_only === true;
    const score =
      (pinned ? 1_000_000 : 0) +
      (a.daily_offer_priority ?? 0) * 10 +
      (premium ? 50 : 0) +
      (a.priority ?? 0);
    const reason: SelectionReason = pinned
      ? "pinned_advertiser"
      : premium
        ? "premium_advertiser"
        : "eligible_advertiser";
    candidates.push({
      score,
      offer: {
        id: a.id,
        source: "advertiser",
        title: a.brand_name,
        description: a.promo_description ?? null,
        image_url: a.logo_url,
        code: a.promo_code,
        external_url: a.affiliate_url ?? a.website_url,
        sponsor_name: a.brand_name,
        sponsor_logo_url: a.logo_url,
        valid_until: a.promo_valid_until,
        selection_reason: reason,
        advertiser_id: a.id,
      },
    });
  }

  if (candidates.length === 0) return null;

  candidates.sort((x, y) => y.score - x.score);
  // Among the top-tier (same top score), random pick for fairness
  const topScore = candidates[0].score;
  const topTier = candidates.filter((c) => c.score === topScore);
  const winner = topTier[Math.floor(Math.random() * topTier.length)].offer;

  // Hydrate sponsor info for community-goal winner
  if (winner.source === "community_goal") {
    const matching = gso.find((o) => o.id === winner.id);
    if (matching?.common_goal_id) {
      const { data: cg } = await supabase
        .from("common_goals")
        .select("sponsor_name, sponsor_logo_url")
        .eq("id", matching.common_goal_id)
        .maybeSingle();
      winner.sponsor_name = cg?.sponsor_name ?? null;
      winner.sponsor_logo_url = cg?.sponsor_logo_url ?? null;
    }
  }

  return winner;
}

export function useDailyOffer() {
  const { user } = useAuth();
  const [shouldShow, setShouldShow] = useState(false);
  const [grace, setGrace] = useState(false);

  useEffect(() => {
    if (!user) return;
    const created = user.created_at ? new Date(user.created_at).getTime() : 0;
    const ageMs = Date.now() - created;
    setGrace(ageMs < NEW_USER_GRACE_DAYS * 24 * 60 * 60 * 1000);
  }, [user]);

  const query = useQuery({
    queryKey: ["dailyOffer", user?.id],
    queryFn: () => selectOffer(user!.id),
    enabled: !!user?.id && !alreadyShownToday() && !grace,
    staleTime: 60 * 60 * 1000,
    retry: 0,
  });

  useEffect(() => {
    if (query.data && !alreadyShownToday() && !grace) {
      const t = setTimeout(() => setShouldShow(true), 1500);
      return () => clearTimeout(t);
    }
  }, [query.data, grace]);

  const dismiss = () => setShouldShow(false);

  return {
    offer: query.data ?? null,
    isOpen: shouldShow && !!query.data,
    dismiss,
    loading: query.isLoading,
  };
}

export async function reportDailyOfferAction(args: {
  offer_id: string;
  offer_source: DailyOfferSource;
  dwell_ms: number;
  action: "copy_code" | "open_site" | "dismiss" | "timeout";
  selection_reason?: SelectionReason;
}): Promise<{ pointsAwarded: number; alreadyClaimedToday: boolean }> {
  try {
    const { data, error } = await supabase.functions.invoke(
      "award-daily-offer-points",
      { body: args },
    );
    if (error) throw error;
    return data as { pointsAwarded: number; alreadyClaimedToday: boolean };
  } catch (e) {
    logger.error("reportDailyOfferAction error", e);
    return { pointsAwarded: 0, alreadyClaimedToday: false };
  }
}
